import { CacheSuccess, CacheEmpty } from '../../types/CacheResponse';

export class Cache {
    constructor({ keyName, ttl = 24 * 60 * 60 * 1000, userId = null }) {
        if (!keyName) {
            throw new Error('keyName is required for Cache initialization');
        }
        this.ttl = ttl;
        this.keyName = keyName;
        this.userId = userId;
        // Include userId in cache key to prevent cross-user data leakage
        this.localStorageKeyName = userId ? 
            `bb_cache_${userId}_${keyName}` : 
            `bb_cache_${keyName}`;
    }

    set(cacheItem) {
        if (!cacheItem.id) {
            throw new Error('Cache items must have an id property');
        }

        // Get existing items or initialize empty array
        let items = this.getAll();

        // Check if items is null or empty
        if (!items || items.length === 0) {
            items = [];
        }

        // Find if item with same id already exists
        const existingIndex = items.findIndex(item => item.id === cacheItem.id);

        if (existingIndex >= 0) {
            // Update existing item
            items[existingIndex] = {
                ...cacheItem,
                timestamp: Date.now()
            };
        } else {
            // Add new item if it doesn't exist
            items.push({
                ...cacheItem,
                timestamp: Date.now()
            });
        }

        // Store the updated array with error handling
        try {
            const dataToStore = JSON.stringify(items);
            
            // Check size limit
            if (dataToStore.length > 4000000) {
                console.warn(`Cache data for ${this.keyName} is too large, skipping storage`);
                return;
            }
            
            localStorage.setItem(this.localStorageKeyName, dataToStore);
        } catch (error) {
            if (error.name === 'QuotaExceededError') {
                console.warn(`LocalStorage quota exceeded for ${this.keyName}, unable to store item`);
            } else {
                console.error(`Error storing cache item for ${this.keyName}:`, error.message);
            }
        }
    }

    get(cacheItemId) {
        if (!cacheItemId) {
            throw new Error('cacheItemId is required for Cache.get. To get all items, use Cache.getAll()');
        }

        const items = this.getAll();

        // Check if items exist and have length
        if (!items) {
            return CacheEmpty();
        }

        if (items.length === 0) {
            return CacheEmpty();
        }

        const item = items.find(item => item.id === cacheItemId);
        if (!item) {
            return CacheEmpty();
        }

        // Check if the item has expired
        if (this.ttl > 0) {
            const now = Date.now();
            if (now - item.timestamp > this.ttl) {
                // Item has expired, remove it
                this.remove(cacheItemId);
                return CacheEmpty();
            }
        }

        return CacheSuccess(item);
    }

    getAll() {
        const jsonItems = localStorage.getItem(this.localStorageKeyName);
        if (!jsonItems) {
            return CacheEmpty();
        }
        let items;
        try {
            items = JSON.parse(jsonItems);
        } catch (_error) {
            this.clear();
            return CacheEmpty();
        }
        if (!Array.isArray(items)) {
            this.clear();
            return CacheEmpty();
        }

        const now = Date.now();

        // Filter out expired items
        if (this.ttl > 0) {
            const validItems = items.length > 0 ? items.filter(item => now - item.timestamp <= this.ttl) : [];

            // If some items were expired, update storage
            if (validItems.length < items.length) {
                if (validItems.length === 0) {
                    localStorage.removeItem(this.localStorageKeyName);
                    return CacheEmpty();
                }
                localStorage.setItem(this.localStorageKeyName, JSON.stringify(validItems));
                return CacheSuccess(validItems);
            }
        }

        return CacheSuccess(items);
    }

    remove(cacheItemId) {
        let items = this.getAll();
        if (!items) {
            return;
        }

        const filteredItems = items.filter(item => item.id !== cacheItemId);

        if (filteredItems.length === 0) {
            localStorage.removeItem(this.localStorageKeyName);
        } else {
            localStorage.setItem(this.localStorageKeyName, JSON.stringify(filteredItems));
        }
    }

    /**
     * Overwrites all items in the cache with new items
     * @param {Array} items - Array of items to store in cache
     * @throws {Error} If items is not an array or if any item is missing an id
     */
    setAll(items) {
        if (!Array.isArray(items)) {
            throw new Error('Items must be an array for Cache.setAll');
        }

        // Validate that all items have an id
        const missingIds = items.filter(item => !item.id);
        if (missingIds.length > 0) {
            throw new Error('All cache items must have an id property');
        }

        const timestampedItems = items.map(item => ({
            ...item,
            timestamp: Date.now()
        }));

        try {
            const dataToStore = JSON.stringify(timestampedItems);
            
            // Check if data size is too large (localStorage limit is ~5-10MB)
            // Estimate: 1 character ≈ 1 byte, so 5MB ≈ 5,000,000 characters
            if (dataToStore.length > 4000000) { // 4MB limit to be safe
                console.warn(`Cache data for ${this.keyName} is too large (${dataToStore.length} chars), skipping cache storage`);
                return;
            }
            
            localStorage.setItem(this.localStorageKeyName, dataToStore);
        } catch (error) {
            if (error.name === 'QuotaExceededError') {
                console.warn(`LocalStorage quota exceeded for ${this.keyName}, clearing cache and retrying`);
                // Try to clear this cache and retry once
                this.clear();
                try {
                    const dataToStore = JSON.stringify(timestampedItems);
                    if (dataToStore.length <= 4000000) {
                        localStorage.setItem(this.localStorageKeyName, dataToStore);
                    } else {
                        console.warn(`Cache data still too large after clearing, skipping storage for ${this.keyName}`);
                    }
                } catch (retryError) {
                    console.warn(`Failed to store cache even after clearing for ${this.keyName}:`, retryError.message);
                }
            } else {
                console.error(`Unexpected error storing cache for ${this.keyName}:`, error.message);
            }
        }
    }

    clear() {
        localStorage.removeItem(this.localStorageKeyName);
    }

    /**
     * Get cache size information
     * @returns {Object} Size information including character count and estimated bytes
     */
    getSizeInfo() {
        try {
            const data = localStorage.getItem(this.localStorageKeyName);
            if (!data) {
                return { exists: false, size: 0, sizeFormatted: '0 B' };
            }
            
            const size = data.length;
            const sizeFormatted = size > 1000000 ? 
                `${(size / 1000000).toFixed(1)} MB` : 
                size > 1000 ? 
                `${(size / 1000).toFixed(1)} KB` : 
                `${size} B`;
                
            return { 
                exists: true, 
                size, 
                sizeFormatted,
                isLarge: size > 2000000 // Flag if over 2MB
            };
        } catch (error) {
            return { exists: false, size: 0, sizeFormatted: '0 B', error: error.message };
        }
    }

    /**
     * Clean up old cache entries to free space
     * Removes entries older than TTL and optionally reduces cache size
     * @param {boolean} aggressive - If true, removes more entries to reduce size
     */
    cleanup(aggressive = false) {
        try {
            const items = this.getAll();
            if (!items || items.length === 0) return;

            const now = Date.now();
            let filteredItems = items.filter(item => {
                // Remove expired items
                if (item.timestamp && (now - item.timestamp) > this.ttl) {
                    return false;
                }
                return true;
            });

            // If aggressive cleanup and still too many items, keep only the most recent ones
            if (aggressive && filteredItems.length > 50) {
                filteredItems = filteredItems
                    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
                    .slice(0, 50); // Keep only 50 most recent items
            }

            // Only update if we actually removed items
            if (filteredItems.length < items.length) {
                this.setAll(filteredItems);
                console.log(`Cache cleanup for ${this.keyName}: removed ${items.length - filteredItems.length} items`);
            }
        } catch (error) {
            console.warn(`Error during cache cleanup for ${this.keyName}:`, error.message);
        }
    }
}
